/**
 * The route table.
 *
 * Five groups:
 *   /rfq/:token                    NOT PART OF THIS APPLICATION at all: an
 *                                  external vendor answering a request for
 *                                  quotation. No shell, no navigation, no
 *                                  session — see below.
 *   /login, /forgot-password, ...  unauthenticated (AuthLayout redirects if
 *                                  already signed in)
 *   /change-password               authenticated but not yet admitted: the
 *                                  mandatory first password change
 *   /org/:orgSlug/*                the application, behind AppLayout
 *   everything else                sent into an organization, path intact
 *
 * WHY THE ORGANIZATION IS IN THE URL
 *
 * The application is multi-tenant, and until now which tenant you were looking
 * at lived in local storage — invisible, unlinkable, and shared between tabs.
 * `/org/<handle>/purchase-orders/<id>` says which organization it belongs to,
 * survives being pasted into a message, and lets two organizations be open
 * side by side. AppLayout treats the handle as authoritative and switches to
 * it; the stored selection now only decides where `/` sends you.
 *
 * Screens still declare organization-relative paths ("suppliers"), and still
 * LINK to organization-relative paths ("/suppliers") — see orgNavigation.jsx
 * for how the prefix is applied without eighty template literals.
 *
 * There is no /register. Accounts are created by a platform administrator (an
 * organization owner, in ERP-ADMIN) or by an owner adding a member, so there
 * is nothing for a stranger to sign up to.
 *
 * Screens are lazily loaded (see lazyPages.js), so the initial bundle is the
 * shell plus the sign-in page rather than the whole application.
 */
import { Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLayout, OrganizationEntry } from '../layouts/AppLayout.jsx';
import { AuthLayout } from '../layouts/AuthLayout.jsx';
import { BrandMark } from '../layouts/Brand.jsx';
import { LoadingState } from '../components/States.jsx';
import { RequireAuth } from './RequireAuth.jsx';
import { RequirePermission } from './RequirePermission.jsx';
import { LoginPage } from '../modules/auth/LoginPage.jsx';
import { ChangePasswordPage } from '../modules/auth/ChangePasswordPage.jsx';
import { ForgotPasswordPage } from '../modules/auth/ForgotPasswordPage.jsx';
import { ResetPasswordPage } from '../modules/auth/ResetPasswordPage.jsx';
import * as pages from './lazyPages.js';

/**
 * The centred auth card, without AuthLayout's redirect.
 *
 * AuthLayout sends a signed-in visitor to the dashboard, which is exactly
 * wrong for /change-password: the user there IS signed in and the dashboard is
 * the one place they must not reach yet.
 */
function AuthShell({ children }) {
  return (
    <div className="auth-shell auth-shell-simple">
      <div className="auth-card">
        <div className="auth-brand">
          <BrandMark size={30} />
          <span className="brand-word">
            Nex<span className="brand-word-strong">ERP</span>
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * The guard with "what" in the interface language: the table names it by a
 * key in navigation:guards.*, and it is looked up when the guard renders. It
 * goes into the "you cannot view {{what}}" sentence, so it is not paired in
 * bilingual mode itself.
 */
function GuardedRoute({ permissions, whatKey, children }) {
  const { t } = useTranslation('navigation');
  return (
    <RequirePermission permissions={permissions} what={t(whatKey, { bilingual: false })}>
      {children}
    </RequirePermission>
  );
}

/** Wraps a screen in its permission guard, so the table stays declarative. */
const guard = (permissions, whatKey, element) => (
  <GuardedRoute permissions={permissions} whatKey={whatKey}>
    {element}
  </GuardedRoute>
);

export function AppRoutes() {
  return (
    <Suspense fallback={<LoadingState />}>
      <Routes>
        {/*
          --- the external vendor RFQ portal -------------------------------

          Declared FIRST, and outside every layout in this file. The person on
          this route is not a user of this application: they hold a link a
          NexERP organization emailed them, they have no account, and they will
          probably never see another screen here.

          So it gets none of the furniture. No AppLayout, because there is no
          organization to put in the sidebar and no navigation that would mean
          anything to them. No AuthLayout, because that redirects a signed-in
          visitor to the dashboard — and a buyer checking what their vendors
          see is a completely reasonable thing to do.

          It is also OUTSIDE the /org prefix, deliberately: the token is the
          whole address. A vendor should not have to be told, or be able to
          learn, which organization handle to put in front of it.

          It deliberately does NOT sit behind RequireAuth or RequirePermission.
          The token in the URL is the credential, and the server proves it
          against a unique index on its SHA-256 before returning anything.
        */}
        <Route path="/rfq/:token" element={<pages.VendorRfqPage />} />

        {/* The customer's quotation link: public, no shell — the token is the credential. */}
        <Route path="/quote-portal/:token" element={<pages.QuotePortalPage />} />

        {/* The customer's invoice & account statement portal: public, no shell — the token is the credential. */}
        <Route path="/customer-portal/:token" element={<pages.CustomerInvoicePortalPage />} />

        {/* The external virtual meeting guest portal: public, no shell — the meeting id/token is the credential. */}
        <Route path="/crm/meet/:id" element={<pages.PublicMeetingPage />} />

        {/*
          --- the public RFQ intake form -----------------------------------

          The SECOND route outside every layout in this file, and outside it for
          the same reasons: the person here found a link on a company's website
          and wants a price. No shell, no navigation, no session, no sign-in.

          It is also EMBEDDABLE. `?embed=1` drops the outer chrome so the form
          can sit inside the organization's own website in an iframe — see
          public/embed/rfq-form.js — which is why it must be a top-level route
          rather than one nested inside anything that adds furniture.

          It deliberately does NOT sit behind RequireAuth or RequirePermission.
          The key in the URL is a public address, not a credential: it resolves
          to exactly one form, and the server derives the organization from
          that row rather than trusting anything the browser says.
        */}
        <Route path="/rfq-form/:publicKey" element={<pages.PublicRfqFormPage />} />

        {/* --- unauthenticated -------------------------------------------- */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* --- authenticated, but not yet admitted ------------------------ */}
        {/*
          The mandatory first password change. Outside the shell because the
          user has no usable session for it yet: their token reaches the
          password-change endpoints and nothing else. Outside /org for the same
          reason — they have not been told which organizations they are in,
          because the call that would answer that is one their token cannot
          make.

          `RequireAuth` (not AuthLayout) because they ARE authenticated —
          AuthLayout would bounce them straight back to the dashboard, which is
          the one place they may not go.
        */}
        <Route
          path="/change-password"
          element={
            <RequireAuth>
              <AuthShell>
                <ChangePasswordPage />
              </AuthShell>
            </RequireAuth>
          }
        />

        {/* --- the application, inside one organization -------------------- */}
        {/*
          Child paths are RELATIVE, so the prefix is written once. AppLayout
          resolves :orgSlug to a membership before any of them render.
        */}
        <Route path="/org/:orgSlug" element={<AppLayout />}>
          <Route index element={<pages.DashboardPage />} />

          {/* --- Analytics & Business Intelligence --- */}
          <Route
            path="analytics"
            element={guard(['analytics.executive.read', 'analytics.read'], 'guards.executiveDashboard', <pages.ExecutiveDashboardPage />)}
          />
          <Route
            path="analytics/financials"
            element={guard(['analytics.financial.read', 'analytics.read'], 'guards.financialIntelligence', <pages.FinancialIntelligencePage />)}
          />
          <Route
            path="analytics/aging-spend"
            element={guard(['analytics.ar.read', 'analytics.spend.read', 'analytics.read'], 'guards.arApAndSpendAnalysis', <pages.AgingAndSpendPage />)}
          />
          <Route
            path="analytics/operations"
            element={guard(['analytics.inventory.read', 'analytics.hr.read', 'analytics.read'], 'guards.operationsAndWorkforceAnalytics', <pages.OperationsAnalyticsPage />)}
          />

          {/*
            --- app homes --------------------------------------------------
            Where opening an app from the launcher lands, for the apps whose
            pages had no landing of their own. Additions only: every screen
            below keeps its address. Procurement's is its dashboard (the same
            figures the workspace home shows), so it needs what they need;
            the others are maps of their app, filtered to what the caller may
            open, so they need nothing.
          */}
          <Route
            path="procurement"
            element={guard(['purchase.read'], 'guards.procurementOverview', <pages.ProcurementOverviewPage />)}
          />
          <Route
            path="procurement/spend-analysis"
            element={guard(['purchase.read', 'analytics.spend.read', 'analytics.read'], 'guards.arApAndSpendAnalysis', <pages.AgingAndSpendPage />)}
          />
          <Route
            path="procurement/vendor-scorecard"
            element={guard(['purchase.read', 'analytics.spend.read', 'analytics.read'], 'guards.arApAndSpendAnalysis', <pages.AgingAndSpendPage />)}
          />
          <Route
            path="purchasing/spend-analysis"
            element={guard(['purchase.read', 'analytics.spend.read', 'analytics.read'], 'guards.arApAndSpendAnalysis', <pages.AgingAndSpendPage />)}
          />
          <Route
            path="purchasing/vendor-scorecard"
            element={guard(['purchase.read', 'analytics.spend.read', 'analytics.read'], 'guards.arApAndSpendAnalysis', <pages.AgingAndSpendPage />)}
          />
          <Route path="people" element={<pages.AppHomePage appKey="people" />} />
          <Route path="master-data" element={<pages.AppHomePage appKey="master-data" />} />
          {/* Direct master-data prefixed routes to support http://localhost:5173/master-data/* URLs seamlessly */}
          <Route path="master-data/items" element={guard(['mdm.read', 'mdm.item.read'], 'guards.items', <pages.ItemListPage />)} />
          <Route path="master-data/items/new" element={guard(['mdm.create', 'mdm.item.create'], 'guards.items', <pages.ItemFormPage />)} />
          <Route path="master-data/items/:id" element={guard(['mdm.read', 'mdm.item.read'], 'guards.items', <pages.ItemDetailPage />)} />
          <Route path="master-data/items/:id/edit" element={guard(['mdm.update', 'mdm.item.update'], 'guards.items', <pages.ItemFormPage />)} />
          <Route path="master-data/warehouses" element={guard(['mdm.read', 'mdm.warehouse.read'], 'guards.warehouses', <pages.WarehouseListPage />)} />
          <Route path="master-data/warehouses/:id" element={guard(['mdm.read', 'mdm.warehouse.read'], 'guards.warehouses', <pages.WarehouseDetailPage />)} />
          <Route path="master-data/partners" element={guard(['mdm.read', 'mdm.partner.read'], 'guards.businessPartners', <pages.PartnerListPage />)} />
          <Route path="master-data/partners/new" element={guard(['mdm.create', 'mdm.partner.create'], 'guards.businessPartners', <pages.PartnerFormPage />)} />
          <Route path="master-data/partners/:id" element={guard(['mdm.read', 'mdm.partner.read'], 'guards.businessPartners', <pages.PartnerDetailPage />)} />
          <Route path="master-data/partners/:id/edit" element={guard(['mdm.update', 'mdm.partner.update'], 'guards.businessPartners', <pages.PartnerFormPage />)} />
          <Route path="master-data/price-lists" element={guard(['mdm.read', 'mdm.price.read'], 'guards.priceLists', <pages.PriceListListPage />)} />
          <Route path="master-data/uoms" element={guard(['mdm.read', 'mdm.uom.read'], 'guards.uoms', <pages.UomListPage />)} />
          <Route path="admin" element={<pages.AppHomePage appKey="admin" />} />

          <Route path="suppliers" element={guard(['supplier.read'], 'guards.suppliers', <pages.SupplierListPage />)} />
          <Route path="suppliers/new" element={guard(['supplier.create'], 'guards.suppliers', <pages.SupplierFormPage />)} />
          <Route path="suppliers/:id" element={guard(['supplier.read'], 'guards.suppliers', <pages.SupplierDetailPage />)} />
          <Route
            path="suppliers/:id/edit"
            element={guard(['supplier.update'], 'guards.suppliers', <pages.SupplierFormPage />)}
          />

          <Route
            path="purchase-requests"
            element={guard(['purchase.read'], 'guards.purchaseRequests', <pages.RequestListPage />)}
          />
          <Route
            path="purchase-requests/new"
            element={guard(['purchase.create'], 'guards.purchaseRequests', <pages.RequestFormPage />)}
          />
          <Route
            path="purchase-requests/:id"
            element={guard(['purchase.read'], 'guards.purchaseRequests', <pages.RequestDetailPage />)}
          />
          <Route
            path="purchase-requests/:id/edit"
            element={guard(['purchase.update'], 'guards.purchaseRequests', <pages.RequestFormPage />)}
          />

          <Route path="rfqs" element={guard(['rfq.read'], 'guards.rfqs', <pages.RfqListPage />)} />
          <Route path="rfqs/new" element={guard(['rfq.create'], 'guards.rfqs', <pages.RfqFormPage />)} />

          {/*
            --- external RFQ intake ---------------------------------------

            Declared BEFORE `rfqs/:id`, and the order is load-bearing: React
            Router would otherwise match `rfqs/intake` as an RFQ whose id is
            the word "intake" and render a 404 from the detail screen.

            Nested under /rfqs rather than given a prefix of its own, because
            an external enquiry is not a different kind of document — it is the
            same RFQ module with a second front door. Everything these screens
            produce is worked through `rfqs/:id` like anything else.
          */}
          <Route
            path="rfqs/intake/forms"
            element={guard(['rfq.intake.read'], 'guards.externalRfqForms', <pages.IntakeFormListPage />)}
          />
          <Route
            path="rfqs/intake/forms/:formId"
            element={guard(['rfq.intake.read'], 'guards.externalRfqForms', <pages.IntakeFormBuilderPage />)}
          />
          {/*
            Guarded on `rfq.intake.READ`, not `review`. Working an enquiry is
            gated inside the page on `rfq.intake.review`, so a read-only
            colleague can see what came in and change nothing — which is a
            useful state, and the reason the two permissions are separate.
          */}
          <Route
            path="rfqs/intake/submissions"
            element={guard(['rfq.intake.read'], 'guards.externalEnquiries', <pages.IntakeSubmissionsPage />)}
          />
          <Route
            path="rfqs/intake/submissions/:submissionId"
            element={guard(['rfq.intake.read'], 'guards.externalEnquiries', <pages.IntakeSubmissionDetailPage />)}
          />

          <Route path="rfqs/:id" element={guard(['rfq.read'], 'guards.rfqs', <pages.RfqDetailPage />)} />
          <Route path="rfqs/:id/edit" element={guard(['rfq.update'], 'guards.rfqs', <pages.RfqFormPage />)} />

          <Route
            path="purchase-orders"
            element={guard(['purchase.read'], 'guards.purchaseOrders', <pages.OrderListPage />)}
          />
          <Route
            path="purchase-orders/new"
            element={guard(['purchase.create'], 'guards.purchaseOrders', <pages.OrderFormPage />)}
          />
          <Route
            path="purchase-orders/:id"
            element={guard(['purchase.read'], 'guards.purchaseOrders', <pages.OrderDetailPage />)}
          />
          <Route
            path="purchase-orders/:id/edit"
            element={guard(['purchase.update'], 'guards.purchaseOrders', <pages.OrderFormPage />)}
          />

          <Route path="goods-receipts" element={guard(['purchase.read'], 'guards.goodsReceipts', <pages.ReceiptListPage />)} />
          <Route
            path="goods-receipts/:id"
            element={guard(['purchase.read'], 'guards.goodsReceipts', <pages.ReceiptDetailPage />)}
          />

          {/*
            --- vendor bills --------------------------------------------------
            `new` and `exceptions` are declared BEFORE `:id`, for the same
            reason as rfqs/intake: otherwise they would be read as bill ids.
          */}
          <Route path="purchase-bills" element={guard(['purchase.bill.read'], 'guards.vendorBills', <pages.BillListPage />)} />
          <Route path="purchase-bills/new" element={guard(['purchase.bill.create'], 'guards.vendorBills', <pages.BillFormPage />)} />
          <Route
            path="purchase-bills/exceptions"
            element={guard(['purchase.bill.read'], 'guards.matchExceptions', <pages.MatchExceptionsPage />)}
          />
          <Route path="purchase-bills/:id" element={guard(['purchase.bill.read'], 'guards.vendorBills', <pages.BillDetailPage />)} />
          <Route path="purchase-bills/:id/edit" element={guard(['purchase.bill.create'], 'guards.vendorBills', <pages.BillFormPage />)} />

          {/*
            --- inventory -------------------------------------------------

            Every read is guarded on inventory.stock.read; the create and edit
            forms on the permission their save needs, so a read-only colleague
            is told plainly rather than shown a form that will be refused.
            Actions on a document (post, approve, receive …) are gated inside
            its page, per status. The server checks all of it again.
          */}
          <Route path="inventory" element={guard(['inventory.stock.read'], 'guards.inventory', <pages.InventoryOverviewPage />)} />
          <Route path="inventory/stock" element={guard(['inventory.stock.read'], 'guards.stock', <pages.StockListPage />)} />
          <Route path="inventory/scanner" element={guard(['inventory.stock.read'], 'guards.inventoryScanner', <pages.WarehouseScannerPage />)} />
          <Route
            path="inventory/stock/items/:itemId"
            element={guard(['inventory.stock.read'], 'guards.stock', <pages.ItemStockPage />)}
          />
          <Route path="inventory/ledger" element={guard(['inventory.stock.read'], 'guards.stockLedger', <pages.LedgerListPage />)} />
          <Route
            path="inventory/ledger/:id"
            element={guard(['inventory.stock.read'], 'guards.stockLedger', <pages.MovementDetailPage />)}
          />
          <Route
            path="inventory/sales-orders"
            element={guard(['inventory.stock.read'], 'guards.orderReservations', <pages.ReservationOrderListPage />)}
          />
          <Route
            path="inventory/sales-orders/:salesOrderId"
            element={guard(['inventory.stock.read'], 'guards.orderReservations', <pages.ReservationOrderDetailPage />)}
          />
          <Route path="inventory/deliveries" element={guard(['inventory.stock.read'], 'guards.deliveries', <pages.DeliveryListPage />)} />
          <Route
            path="inventory/deliveries/new"
            element={guard(['inventory.delivery.create'], 'guards.deliveries', <pages.DeliveryFormPage />)}
          />
          <Route
            path="inventory/deliveries/:id"
            element={guard(['inventory.stock.read'], 'guards.deliveries', <pages.DeliveryDetailPage />)}
          />
          <Route
            path="inventory/deliveries/:id/edit"
            element={guard(['inventory.delivery.create'], 'guards.deliveries', <pages.DeliveryFormPage />)}
          />
          <Route path="inventory/transfers" element={guard(['inventory.stock.read'], 'guards.transfers', <pages.TransferListPage />)} />
          <Route
            path="inventory/transfers/new"
            element={guard(['inventory.transfer.create'], 'guards.transfers', <pages.TransferFormPage />)}
          />
          <Route
            path="inventory/transfers/:id"
            element={guard(['inventory.stock.read'], 'guards.transfers', <pages.TransferDetailPage />)}
          />
          <Route
            path="inventory/transfers/:id/edit"
            element={guard(['inventory.transfer.create'], 'guards.transfers', <pages.TransferFormPage />)}
          />
          <Route
            path="inventory/adjustments"
            element={guard(['inventory.stock.read'], 'guards.adjustments', <pages.AdjustmentListPage />)}
          />
          <Route
            path="inventory/adjustments/new"
            element={guard(['inventory.adjustment.create'], 'guards.adjustments', <pages.AdjustmentFormPage />)}
          />
          <Route
            path="inventory/adjustments/:id"
            element={guard(['inventory.stock.read'], 'guards.adjustments', <pages.AdjustmentDetailPage />)}
          />
          <Route
            path="inventory/adjustments/:id/edit"
            element={guard(['inventory.adjustment.create'], 'guards.adjustments', <pages.AdjustmentFormPage />)}
          />
          <Route path="inventory/counts" element={guard(['inventory.stock.read'], 'guards.stockCounts', <pages.CountListPage />)} />
          <Route
            path="inventory/counts/new"
            element={guard(['inventory.count.create'], 'guards.stockCounts', <pages.CountFormPage />)}
          />
          <Route
            path="inventory/counts/:id"
            element={guard(['inventory.stock.read'], 'guards.stockCounts', <pages.CountDetailPage />)}
          />
          <Route
            path="inventory/counts/:id/edit"
            element={guard(['inventory.count.create'], 'guards.stockCounts', <pages.CountFormPage />)}
          />
          <Route path="inventory/returns" element={guard(['inventory.stock.read'], 'guards.customerReturns', <pages.ReturnListPage />)} />
          <Route
            path="inventory/returns/:id"
            element={guard(['inventory.stock.read'], 'guards.customerReturns', <pages.ReturnDetailPage />)}
          />
          <Route
            path="inventory/receipts"
            element={guard(['inventory.stock.read'], 'guards.stockReceipts', <pages.StockReceiptListPage />)}
          />
          <Route
            path="inventory/receipts/:id"
            element={guard(['inventory.stock.read'], 'guards.stockReceipts', <pages.StockReceiptDetailPage />)}
          />
          <Route path="inventory/batches" element={guard(['inventory.stock.read'], 'guards.batches', <pages.BatchListPage />)} />
          <Route path="inventory/serials" element={guard(['inventory.stock.read'], 'guards.serialNumbers', <pages.SerialListPage />)} />
          <Route
            path="inventory/valuation"
            element={guard(['inventory.valuation.view'], 'guards.stockValuation', <pages.ValuationPage />)}
          />
          {/* Costs and values: readable with inventory.valuation.view, even without inventory.stock.read. */}
          <Route
            path="inventory/landed-costs"
            element={guard(['inventory.valuation.view'], 'guards.landedCosts', <pages.LandedCostListPage />)}
          />
          <Route
            path="inventory/landed-costs/new"
            element={guard(['inventory.landed-cost.create'], 'guards.landedCosts', <pages.LandedCostFormPage />)}
          />
          <Route
            path="inventory/landed-costs/:id"
            element={guard(['inventory.valuation.view'], 'guards.landedCosts', <pages.LandedCostDetailPage />)}
          />
          <Route
            path="inventory/landed-costs/:id/edit"
            element={guard(['inventory.landed-cost.create'], 'guards.landedCosts', <pages.LandedCostFormPage />)}
          />
          <Route
            path="inventory/reconciliations"
            element={guard(['inventory.valuation.view'], 'guards.stockReconciliation', <pages.StockReconciliationListPage />)}
          />
          <Route
            path="inventory/reconciliations/:id"
            element={guard(['inventory.valuation.view'], 'guards.stockReconciliation', <pages.StockReconciliationDetailPage />)}
          />
          <Route
            path="inventory/reports/abc"
            element={guard(['inventory.valuation.view'], 'guards.abcAnalysis', <pages.AbcReportPage />)}
          />
          <Route
            path="inventory/reports/slow-moving"
            element={guard(['inventory.valuation.view'], 'guards.slowMovingStockReport', <pages.SlowMovingReportPage />)}
          />
          <Route
            path="inventory/reorder"
            element={guard(['inventory.stock.read'], 'guards.reorderSuggestions', <pages.ReorderPage />)}
          />
          <Route
            path="inventory/exceptions"
            element={guard(['inventory.stock.read'], 'guards.inventoryExceptions', <pages.ExceptionListPage />)}
          />
          {/* Read-only without inventory.settings.manage; every write is gated inside. */}
          <Route
            path="inventory/settings"
            element={guard(['inventory.stock.read'], 'guards.inventorySettings', <pages.InventorySettingsPage />)}
          />

          {/* --- crm -------------------------------------------------------- */}
          <Route
            path="crm/dashboard"
            element={guard(
              ['crm.lead.read', 'crm.opportunity.read', 'crm.contact.read', 'crm.activity.read', 'crm.campaign.read'],
              'guards.crmOverview',
              <pages.CrmDashboardPage />
            )}
          />
          <Route path="crm/leads" element={guard(['crm.lead.read'], 'guards.leads', <pages.LeadsPage />)} />
          <Route path="crm/leads/new" element={guard(['crm.lead.create'], 'guards.leads', <pages.LeadFormPage />)} />
          <Route path="crm/leads/:id/edit" element={guard(['crm.lead.update'], 'guards.leads', <pages.LeadFormPage />)} />
          <Route path="crm/opportunities" element={guard(['crm.opportunity.read'], 'guards.opportunities', <pages.OpportunitiesPage />)} />
          <Route path="crm/opportunities/all-boards" element={guard(['crm.opportunity.read'], 'guards.opportunities', <pages.AllBoardsPage />)} />
          <Route path="crm/opportunities/new-board" element={guard(['crm.opportunity.create'], 'guards.opportunities', <pages.NewBoardPage />)} />
          <Route path="crm/contacts" element={guard(['crm.contact.read'], 'guards.contacts', <pages.ContactsPage />)} />
          <Route path="crm/activities" element={guard(['crm.activity.read'], 'guards.activities', <pages.ActivitiesPage />)} />
          <Route path="crm/meetings" element={guard(['crm.activity.read'], 'guards.meetings', <pages.MeetingsPage />)} />
          <Route
            path="crm/meetings/:id/room"
            element={guard(['crm.activity.read'], 'guards.virtualMeetingRoom', <pages.VirtualMeetingRoomPage />)}
          />
          <Route path="crm/campaigns" element={guard(['crm.campaign.read'], 'guards.campaigns', <pages.CampaignsPage />)} />
          {/* --- master data management ---------------------------------- */}
          <Route path="items" element={guard(['mdm.read', 'mdm.item.read'], 'guards.items', <pages.ItemListPage />)} />
          <Route path="items/new" element={guard(['mdm.create', 'mdm.item.create'], 'guards.items', <pages.ItemFormPage />)} />
          <Route path="items/:id" element={guard(['mdm.read', 'mdm.item.read'], 'guards.items', <pages.ItemDetailPage />)} />
          <Route path="items/:id/edit" element={guard(['mdm.update', 'mdm.item.update'], 'guards.items', <pages.ItemFormPage />)} />

          <Route path="uoms" element={guard(['mdm.read', 'mdm.uom.read'], 'guards.unitsOfMeasure', <pages.UomListPage />)} />
          <Route path="uoms/new" element={guard(['mdm.create', 'mdm.uom.manage'], 'guards.unitsOfMeasure', <pages.UomFormPage />)} />
          <Route path="uoms/:id/edit" element={guard(['mdm.update', 'mdm.uom.manage'], 'guards.unitsOfMeasure', <pages.UomFormPage />)} />

          <Route path="tax-codes" element={guard(['mdm.read', 'mdm.tax.read', 'accounting.tax-code.read'], 'guards.taxCodes', <pages.TaxCodeListPage />)} />
          <Route path="tax-codes/new" element={guard(['mdm.create', 'mdm.tax.manage', 'accounting.tax-code.create'], 'guards.taxCodes', <pages.TaxCodeFormPage />)} />
          <Route path="tax-codes/:id/edit" element={guard(['mdm.update', 'mdm.tax.manage', 'accounting.tax-code.update'], 'guards.taxCodes', <pages.TaxCodeFormPage />)} />

          <Route path="price-lists" element={guard(['mdm.read', 'mdm.price.read'], 'guards.priceLists', <pages.PriceListListPage />)} />
          <Route path="price-lists/new" element={guard(['mdm.create', 'mdm.price.manage'], 'guards.priceLists', <pages.PriceListListPage initialCreate />)} />
          <Route path="price-lists/:id" element={guard(['mdm.read', 'mdm.price.read'], 'guards.priceLists', <pages.PriceListDetailPage />)} />

          <Route path="currencies" element={guard(['mdm.read', 'mdm.currency.read', 'accounting.exchange-rate.read'], 'guards.currencies', <pages.CurrencyListPage />)} />

          <Route path="warehouses" element={guard(['mdm.read', 'mdm.warehouse.read'], 'guards.warehouses', <pages.WarehouseListPage />)} />
          <Route path="warehouses/:id" element={guard(['mdm.read', 'mdm.warehouse.read'], 'guards.warehouses', <pages.WarehouseDetailPage />)} />

          <Route path="quotations" element={guard(['sales.quote.read'], 'guards.quotations', <pages.QuotationListPage />)} />
          <Route path="sales-orders" element={guard(['sales.order.read'], 'guards.salesOrders', <pages.SalesOrderListPage />)} />
          <Route path="sales-orders/:id" element={guard(['sales.order.read'], 'guards.salesOrders', <pages.SalesOrderDetailPage />)} />
          <Route path="sales-returns" element={guard(['sales.rma.read'], 'guards.returns', <pages.SalesReturnListPage />)} />
          <Route path="sales-returns/:id" element={guard(['sales.rma.read'], 'guards.returns', <pages.SalesReturnDetailPage />)} />
          <Route path="sales-approvals" element={guard(['sales.quote.approve', 'sales.quote.reject', 'sales.order.amend'], 'guards.approvals', <pages.ApprovalInboxPage />)} />
          <Route path="sales" element={guard(['sales.reports.read'], 'guards.salesOverview', <pages.SalesDashboardPage />)} />
          <Route path="sales/reports" element={guard(['sales.reports.read'], 'guards.salesReports', <pages.SalesReportsPage />)} />
          <Route path="sales-rules" element={guard(['sales.approval.manage'], 'guards.approvalRules', <pages.ApprovalRulesPage />)} />
          <Route path="sales-invoices" element={guard(['sales.invoice.read'], 'guards.invoices', <pages.InvoiceListPage />)} />
          <Route path="sales-recurring-invoices" element={guard(['sales.invoice.read'], 'guards.recurringInvoices', <pages.RecurringInvoicePage />)} />
          <Route path="sales-invoices/:id" element={guard(['sales.invoice.read'], 'guards.invoices', <pages.InvoiceDetailPage />)} />
          <Route path="sales-invoices/:id/print" element={guard(['sales.invoice.read'], 'guards.invoices', <pages.InvoicePrintPage />)} />
          <Route path="sales-credit-notes" element={guard(['sales.invoice.read'], 'guards.creditNotes', <pages.CreditNoteListPage />)} />
          <Route path="sales-settings/credit" element={guard(['sales.settings.manage', 'sales.order.read'], 'guards.creditSettings', <pages.CreditSettingsPage />)} />
          <Route path="quotations/new" element={guard(['sales.quote.create'], 'guards.quotations', <pages.QuotationFormPage />)} />
          <Route path="quotations/:id/edit" element={guard(['sales.quote.update'], 'guards.quotations', <pages.QuotationFormPage />)} />
          <Route path="quotations/:id" element={guard(['sales.quote.read'], 'guards.quotations', <pages.QuotationDetailPage />)} />
          <Route path="customers" element={guard(['mdm.read', 'mdm.partner.read'], 'guards.customers', <pages.PartnerListPage />)} />
          <Route path="customers/new" element={guard(['mdm.create', 'mdm.partner.create'], 'guards.customers', <pages.PartnerFormPage />)} />
          <Route path="customers/:id" element={guard(['mdm.read', 'mdm.partner.read'], 'guards.customers', <pages.PartnerDetailPage />)} />
          <Route path="customers/:id/edit" element={guard(['mdm.update', 'mdm.partner.update'], 'guards.customers', <pages.PartnerFormPage />)} />
          <Route path="partners" element={guard(['mdm.read', 'mdm.partner.read'], 'guards.businessPartners', <pages.PartnerListPage />)} />
          <Route path="partners/new" element={guard(['mdm.create', 'mdm.partner.create'], 'guards.businessPartners', <pages.PartnerFormPage />)} />
          <Route path="partners/:id" element={guard(['mdm.read', 'mdm.partner.read'], 'guards.businessPartners', <pages.PartnerDetailPage />)} />
          <Route path="partners/:id/edit" element={guard(['mdm.update', 'mdm.partner.update'], 'guards.businessPartners', <pages.PartnerFormPage />)} />
          <Route path="vendors" element={guard(['mdm.read', 'mdm.partner.read'], 'guards.vendors', <pages.PartnerListPage />)} />
          <Route path="vendors/new" element={guard(['mdm.create', 'mdm.partner.create'], 'guards.vendors', <pages.PartnerFormPage />)} />
          <Route path="vendors/:id" element={guard(['mdm.read', 'mdm.partner.read'], 'guards.vendors', <pages.PartnerDetailPage />)} />
          <Route path="vendors/:id/edit" element={guard(['mdm.update', 'mdm.partner.update'], 'guards.vendors', <pages.PartnerFormPage />)} />
          <Route path="dual-partners" element={guard(['mdm.read', 'mdm.partner.read'], 'guards.dualPartners', <pages.PartnerListPage />)} />
          <Route path="dual-partners/new" element={guard(['mdm.create', 'mdm.partner.create'], 'guards.dualPartners', <pages.PartnerFormPage />)} />
          <Route path="dual-partners/:id" element={guard(['mdm.read'], 'guards.dualPartners', <pages.PartnerDetailPage />)} />
          <Route path="dual-partners/:id/edit" element={guard(['mdm.update'], 'guards.dualPartners', <pages.PartnerFormPage />)} />

          <Route path="payment-terms" element={guard(['mdm.read'], 'guards.paymentTerms', <pages.PaymentTermListPage />)} />
          <Route path="cost-centers" element={guard(['mdm.read'], 'guards.costCenters', <pages.CostCenterListPage />)} />
          <Route path="shipping-methods" element={guard(['mdm.read'], 'guards.shippingMethods', <pages.ShippingMethodListPage />)} />
          <Route path="terms-templates" element={guard(['mdm.read'], 'guards.termsTemplates', <pages.TermsTemplateListPage />)} />

          {/* --- employee self-service ----------------------------------- */}
            <Route path="my" element={<Navigate to="attendance" replace />} />
            <Route path="my/dashboard" element={<Navigate to="../attendance" replace />} />
            <Route path="my/tasks" element={<pages.MyTasksPage />} />
            <Route path="my/attendance" element={<pages.MyAttendancePage />} />
            <Route path="my/leaves" element={<pages.MyLeavesPage />} />
            <Route path="my/timesheets" element={<pages.MyTimesheetsPage />} />
            <Route path="my/payslips" element={<pages.MyPaySlipsPage />} />
            <Route path="my/expenses" element={<pages.MyExpensesPage />} />
            <Route path="my/profile" element={<pages.MyProfilePage />} />

            {/* --- manager self-service ------------------------------------ */}
            <Route path="team/dashboard" element={guard(['hr.team.read'], 'team dashboard', <pages.TeamDashboardPage />)} />
            <Route path="team/tasks" element={guard(['hr.team.read'], 'team tasks', <pages.TeamTasksPage />)} />
            <Route path="team/members" element={guard(['hr.team.read'], 'team dashboard', <pages.TeamDashboardPage />)} />
            <Route path="team/attendance" element={guard(['hr.team.read'], 'team attendance', <pages.TeamAttendancePage />)} />
            <Route path="team/approvals" element={guard(['hr.team.read'], 'team approvals', <pages.TeamApprovalsPage />)} />

            {/* --- human resources administration -------------------------- */}
          <Route path="employees" element={guard(['hr.read'], 'guards.employees', <pages.EmployeeListPage />)} />
          <Route path="employees/new" element={guard(['hr.create'], 'guards.employees', <pages.EmployeeFormPage />)} />
          <Route path="employees/:id" element={guard(['hr.read'], 'guards.employees', <pages.EmployeeDetailPage />)} />
          <Route path="employees/:id/edit" element={guard(['hr.update'], 'guards.employees', <pages.EmployeeFormPage />)} />
          <Route path="employees/:id/separation" element={guard(['hr.read'], 'guards.employees', <pages.EmployeeSeparationPage />)} />
<Route path="separations" element={guard(['hr.read'], 'guards.employees', <pages.SeparationListPage />)} />

          <Route path="attendance" element={guard(['hr.read'], 'guards.attendance', <pages.AttendanceListPage />)} />
          <Route path="timesheets" element={guard(['hr.read'], 'guards.timesheets', <pages.TimesheetListPage />)} />
          <Route path="payroll" element={guard(['hr.read'], 'guards.payroll', <pages.PayrollComponentsPage />)} />
          <Route path="pay-runs" element={guard(['hr.read'], 'guards.payRuns', <pages.PayRunListPage />)} />
          <Route path="holidays" element={guard(['hr.read'], 'guards.holidays', <pages.HolidayListPage />)} />
          <Route path="expenses" element={guard(['hr.read'], 'guards.expenses', <pages.ExpenseListPage />)} />
          <Route path="document-types" element={guard(['hr.read'], 'guards.documentTypes', <pages.DocumentTypeListPage />)} />
          <Route path="shifts" element={guard(['hr.read'], 'guards.shifts', <pages.ShiftListPage />)} />

          <Route path="teams" element={guard(['hr.read'], 'guards.teams', <pages.TeamListPage />)} />
          <Route path="teams/new" element={guard(['hr.create'], 'guards.teams', <pages.TeamFormPage />)} />
          <Route path="teams/:id" element={guard(['hr.read'], 'guards.teams', <pages.TeamDetailPage />)} />
          <Route path="teams/:id/edit" element={guard(['hr.update'], 'guards.teams', <pages.TeamFormPage />)} />
          <Route path="org-chart" element={guard(['hr.read'], 'guards.orgChart', <pages.OrgChartPage />)} />
          <Route path="departments" element={guard(['hr.read'], 'guards.departments', <pages.DepartmentListPage />)} />
          <Route path="departments/new" element={guard(['hr.create'], 'guards.departments', <pages.DepartmentFormPage />)} />
          <Route path="departments/:id/edit" element={guard(['hr.update'], 'guards.departments', <pages.DepartmentFormPage />)} />

          <Route path="designations" element={guard(['hr.read'], 'guards.designations', <pages.DesignationListPage />)} />
          <Route path="designations/new" element={guard(['hr.create'], 'guards.designations', <pages.DesignationFormPage />)} />
          <Route path="designations/:id/edit" element={guard(['hr.update'], 'guards.designations', <pages.DesignationFormPage />)} />

          <Route path="leaves" element={guard(['hr.read'], 'guards.leaves', <pages.LeaveListPage />)} />
          <Route path="leave-types" element={guard(['hr.read'], 'guards.leavePolicies', <pages.LeaveTypeListPage />)} />

          {/* --- Accounting Module Routes --- */}
          <Route
            path="accounting"
            element={guard(['accounting.account.read', 'accounting.read'], 'guards.accounting', <pages.AccountingOverviewPage />)}
          />
          <Route
            path="accounting/accounts"
            element={guard(['accounting.account.read', 'accounting.read'], 'guards.chartOfAccounts', <pages.AccountListPage />)}
          />
          <Route
            path="accounting/accounts/:accountId/ledger"
            element={guard(['accounting.account.read', 'accounting.read'], 'guards.accountLedger', <pages.AccountLedgerPage />)}
          />
          <Route
            path="accounting/journals"
            element={guard(['accounting.journal.read', 'accounting.read'], 'guards.journalEntries', <pages.JournalListPage />)}
          />
          <Route
            path="accounting/journals/new"
            element={guard(['accounting.journal.create', 'accounting.create'], 'guards.createJournalEntry', <pages.JournalFormPage />)}
          />
          <Route
            path="accounting/journals/:journalId"
            element={guard(['accounting.journal.read', 'accounting.read'], 'guards.journalEntryDetail', <pages.JournalDetailPage />)}
          />
          <Route
            path="accounting/fiscal"
            element={guard(['accounting.period.read', 'accounting.read'], 'guards.fiscalPeriods', <pages.FiscalPage />)}
          />
          <Route
            path="accounting/ar"
            element={guard(['accounting.ar.read', 'accounting.read'], 'guards.accountsReceivable', <pages.ArPage />)}
          />
          <Route
            path="accounting/ap"
            element={guard(['accounting.ap.read', 'accounting.read'], 'guards.accountsPayable', <pages.ApPage />)}
          />
          <Route
            path="accounting/banking"
            element={guard(['accounting.banking.read', 'accounting.read'], 'guards.bankingReconciliation', <pages.BankingPage />)}
          />
          <Route
            path="accounting/tax"
            element={guard(['accounting.tax-code.read', 'accounting.read'], 'guards.taxCodesReports', <pages.TaxPage />)}
          />
          <Route
            path="accounting/fx"
            element={guard(['accounting.exchange-rate.read', 'accounting.read'], 'guards.currencyFx', <pages.FxPage />)}
          />
          <Route
            path="accounting/posting-rules"
            element={guard(['accounting.posting-rule.read', 'accounting.read'], 'guards.postingRules', <pages.PostingRuleListPage />)}
          />
          <Route
            path="accounting/posting-exceptions"
            element={guard(['accounting.posting-exception.read', 'accounting.read'], 'guards.postingExceptions', <pages.PostingExceptionListPage />)}
          />
          <Route
            path="accounting/reports"
            element={guard(['accounting.account.read', 'accounting.read'], 'guards.financialReports', <pages.FinancialReportsPage />)}
          />


          <Route
            path="organization"
            element={guard(['tenant.read'], 'guards.organizationProfile', <pages.OrganizationPage />)}
          />
          <Route path="organization/branches" element={guard(['branch.read'], 'guards.branches', <pages.BranchesPage />)} />

          <Route path="users" element={guard(['member.read'], 'guards.people', <pages.MembersPage />)} />
          <Route path="roles" element={guard(['role.read', 'member.read'], 'guards.roles', <pages.RolesPage />)} />

          <Route path="settings" element={guard(['settings.read'], 'guards.settings', <pages.SettingsPage />)} />
          {/*
            Guarded on rfq.config.READ, not update. The screen is useful to a
            buyer who wants to know why a field is mandatory or which rule is
            going to demand an approval, and it renders read-only for them —
            every write is gated inside the page on rfq.config.update.
          */}
          <Route
            path="settings/rfq"
            element={guard(['rfq.config.read'], 'guards.rfqSettings', <pages.RfqSettingsPage />)}
          />
          {/* Receiving and matching tolerances: readable with purchase.read, written with purchase.settings.manage. */}
          <Route
            path="settings/purchasing"
            element={guard(['purchase.read', 'purchase.settings.manage'], 'guards.purchasingSettings', <pages.PurchaseSettingsPage />)}
          />
          {/* Open to every member: without audit.read the page shows only
              their own activity, which the server lets anyone read. */}
          <Route path="activity" element={<pages.ActivityPage />} />
          {/* Your own account needs no organization permission. */}
          <Route path="account" element={<pages.AccountPage />} />

          {/* External portal links opened while inside an organization session: redirect to public portal */}
          <Route path="customer-portal/:token" element={<CustomerPortalOrgRedirect />} />
          <Route path="quote-portal/:token" element={<QuotePortalOrgRedirect />} />

          <Route path="*" element={<pages.NotFoundPage />} />
        </Route>

        {/*
          --- the way in ---------------------------------------------------

          '/' plus every path that matched nothing above. The second part is
          what keeps the old, un-prefixed links alive: OrganizationEntry moves
          them under the organization the user last had open instead of
          answering 404. A path that is genuinely unknown ends up at the 404
          inside the shell, which is the more useful of the two — it has the
          navigation on it.
        */}
        <Route path="/" element={<OrganizationEntry />} />
        <Route path="*" element={<OrganizationEntry />} />
      </Routes>
    </Suspense>
  );
}

function CustomerPortalOrgRedirect() {
  const { token } = useParams();
  return <Navigate to={`/customer-portal/${token}`} replace />;
}

function QuotePortalOrgRedirect() {
  const { token } = useParams();
  return <Navigate to={`/quote-portal/${token}`} replace />;
}
